import {
  TERMINAL_RUN_STATES,
  type AvailableConversationEntry,
  type PortalStreamEvent,
  type RunCommand,
  type RunSnapshot,
  type RunState,
} from "./api";
import type { ExecutionStep } from "./ExecutionRail";

export type ConnectionState =
  | "unavailable"
  | "no-run"
  | "loading"
  | "connected"
  | "reconnecting"
  | "interrupted"
  | "error";

export type RevokeState =
  | "idle"
  | "confirming"
  | "applying"
  | "succeeded"
  | "failed";

export type RunUiState = {
  connection: ConnectionState;
  run: RunSnapshot | null;
  chunks: Array<{
    sequence: number;
    text: string;
    occurredAt: string;
  }>;
  cursor: number;
  errorCode: string | null;
  revoke: RevokeState;
};

export type RunUiAction =
  | { type: "no_gateway" }
  | { type: "no_run" }
  | { type: "load_started" }
  | { type: "snapshot_loaded"; snapshot: RunSnapshot }
  | { type: "stream_event"; event: PortalStreamEvent }
  | { type: "reconnecting" }
  | { type: "stream_interrupted"; code: string }
  | { type: "failed"; code: string; unavailable?: boolean }
  | { type: "revoke_confirming" }
  | { type: "revoke_dismissed" }
  | { type: "revoke_started" }
  | { type: "revoke_completed"; command: RunCommand }
  | { type: "revoke_failed"; code: string; unavailable?: boolean };

export const initialRunUiState: RunUiState = {
  connection: "unavailable",
  run: null,
  chunks: [],
  cursor: 0,
  errorCode: null,
  revoke: "idle",
};

const snapshotWithStreamState = (
  snapshot: RunSnapshot,
  event: Extract<PortalStreamEvent, { kind: "run-state" }>,
): RunSnapshot => ({
  ...snapshot,
  state: event.state,
  result_state: event.result_state,
  usage_state: event.usage_state,
  last_sequence: Math.max(snapshot.last_sequence, event.sequence),
  updated_at: event.occurred_at,
  terminal_at: TERMINAL_RUN_STATES.has(event.state)
    ? event.occurred_at
    : null,
});

export const runUiReducer = (
  state: RunUiState,
  action: RunUiAction,
): RunUiState => {
  switch (action.type) {
    case "no_gateway":
      return initialRunUiState;
    case "no_run":
      return {
        ...initialRunUiState,
        connection: "no-run",
      };
    case "load_started":
      return {
        ...initialRunUiState,
        connection: "loading",
      };
    case "snapshot_loaded":
      return {
        ...state,
        connection: "connected",
        run: action.snapshot,
        errorCode: null,
      };
    case "stream_event": {
      const event = action.event;
      if (event.kind === "stream-control") {
        return {
          ...state,
          connection: "interrupted",
          errorCode: event.code,
        };
      }
      if (event.sequence <= state.cursor) {
        return state;
      }
      if (event.sequence !== state.cursor + 1) {
        return {
          ...state,
          connection: "error",
          errorCode: "STREAM_SEQUENCE_GAP",
        };
      }
      if (
        state.run &&
        TERMINAL_RUN_STATES.has(state.run.state) &&
        event.kind === "chunk" &&
        state.revoke !== "succeeded" &&
        state.run.state !== "REVOKED" &&
        event.sequence > state.run.last_sequence
      ) {
        return {
          ...state,
          connection: "error",
          errorCode: "STREAM_TERMINAL_BOUND_EXCEEDED",
        };
      }
      if (
        state.run &&
        TERMINAL_RUN_STATES.has(state.run.state) &&
        ((event.kind === "chunk" &&
          (state.revoke === "succeeded" ||
            state.run.state === "REVOKED")) ||
          (event.kind === "run-state" &&
            !TERMINAL_RUN_STATES.has(event.state)))
      ) {
        return {
          ...state,
          cursor: event.sequence,
        };
      }
      if (event.kind === "chunk") {
        return {
          ...state,
          connection: "connected",
          cursor: event.sequence,
          chunks: [
            ...state.chunks,
            {
              sequence: event.sequence,
              text: event.text,
              occurredAt: event.occurred_at,
            },
          ],
        };
      }
      return {
        ...state,
        connection: "connected",
        cursor: event.sequence,
        run: state.run
          ? snapshotWithStreamState(state.run, event)
          : state.run,
      };
    }
    case "reconnecting":
      if (state.run && TERMINAL_RUN_STATES.has(state.run.state)) {
        return state;
      }
      return {
        ...state,
        connection: "reconnecting",
        errorCode: null,
      };
    case "stream_interrupted":
      return {
        ...state,
        connection: "interrupted",
        errorCode: action.code,
      };
    case "failed":
      return {
        ...state,
        connection: action.unavailable ? "unavailable" : "error",
        errorCode: action.code,
      };
    case "revoke_confirming":
      return { ...state, revoke: "confirming" };
    case "revoke_dismissed":
      return { ...state, revoke: "idle" };
    case "revoke_started":
      return { ...state, revoke: "applying" };
    case "revoke_completed":
      if (!state.run || action.command.run_id !== state.run.run_id) {
        return {
          ...state,
          revoke: "failed",
          errorCode: "REVOCATION_PROTOCOL_ERROR",
        };
      }
      return {
        ...state,
        connection: "connected",
        run: {
          ...state.run,
          state: action.command.state,
          result_state:
            state.chunks.length > 0 ? "PARTIAL" : state.run.result_state,
        },
        revoke: "succeeded",
        errorCode: null,
      };
    case "revoke_failed":
      return {
        ...state,
        connection: action.unavailable
          ? "unavailable"
          : state.connection,
        revoke: "failed",
        errorCode: action.code,
      };
  }
};

const stateLabel: Record<RunState, string> = {
  QUEUED: "În coadă",
  RUNNING: "Provider activ",
  STREAMING: "Răspuns live",
  SUCCEEDED: "Finalizată",
  FAILED: "Eșuată",
  CANCELLED: "Anulată",
  REVOKED: "Revocată",
  EXPIRED: "Expirată",
};

export const runStateLabel = (state: RunState): string => stateLabel[state];

export const shortResourceId = (identifier: string): string =>
  `…${identifier.slice(-8)}`;

export const conversationLabel = (conversationId: string): string =>
  `Conversație ${shortResourceId(conversationId)}`;

export const historyRoleLabel = (
  role: AvailableConversationEntry["role"],
): string => (role === "USER" ? "Tu" : "Asistent organizațional");

export const formatServerDateTime = (value: string): string =>
  new Intl.DateTimeFormat("ro-RO", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value));

const historyFailureCopy: Readonly<Record<string, string>> = {
  INVALID_HISTORY_CURSOR:
    "Pagina de istoric nu mai este valabilă. Reîncarcă lista de la început.",
  INVALID_HISTORY_PAGE:
    "Dimensiunea paginii de istoric a fost refuzată de serviciu.",
  HISTORY_PROTOCOL_ERROR:
    "Răspunsul istoricului nu respectă contractul versionat și nu a fost afișat.",
  HISTORY_ENTRY_NOT_FOUND:
    "Intrarea nu mai este disponibilă pentru această sesiune.",
  CONVERSATION_NOT_FOUND:
    "Conversația nu mai este disponibilă pentru această sesiune.",
  HISTORY_ENTRY_ACTIVE:
    "Intrarea aparține unei rulări active și nu poate fi ștearsă încă.",
  SESSION_ALREADY_CURRENT:
    "Conversația folosește deja această sesiune; nu este necesar un intent nou.",
  HISTORY_CONTEXT_TOO_LARGE:
    "Istoricul vizibil depășește limita sigură pentru reluare.",
  RESUME_INTENT_UNAVAILABLE:
    "Intentul de reluare nu mai este valabil. Pregătește reluarea din nou.",
  HISTORY_CHANGED_REPREPARE:
    "Istoricul s-a schimbat. Pregătește reluarea din nou.",
  SESSION_AUTHORIZATION_STALE:
    "Drepturile sesiunii s-au schimbat. Reautentificarea este necesară.",
  CSRF_DENIED:
    "Sesiunea browser nu mai poate autoriza acțiunea.",
  SERVICE_UNAVAILABLE:
    "Serviciul de istoric nu este disponibil în această sesiune.",
  PORTAL_NETWORK_ERROR:
    "Conexiunea cu istoricul nu a putut fi refăcută.",
};

export const historyFailureMessage = (code: string | null): string =>
  (code && historyFailureCopy[code]) ??
  "Operația asupra istoricului nu a fost confirmată.";

export const executionStepsFor = (
  run: RunSnapshot | null,
): ExecutionStep[] => {
  if (!run) {
    return [
      ["request", "Cerere", "Neinițiată"],
      ["provider", "Provider", "Neapelat"],
      ["stream", "Streaming", "Inactiv"],
      ["result", "Rezultat", "Indisponibil"],
      ["usage", "Cost și audit", "Fără eveniment"],
    ].map(([id, label, detail]) => ({
      id: id as ExecutionStep["id"],
      label,
      detail,
      state: "idle",
    }));
  }

  const failed = run.state === "FAILED" || run.state === "EXPIRED";
  const revoked = run.state === "REVOKED" || run.state === "CANCELLED";
  const streamed = run.last_sequence > 0 || run.state === "STREAMING";
  const terminal = TERMINAL_RUN_STATES.has(run.state);
  return [
    {
      id: "request",
      label: "Cerere",
      detail: "Acceptată",
      state: "complete",
    },
    {
      id: "provider",
      label: "Provider",
      detail:
        run.state === "QUEUED"
          ? "În așteptare"
          : terminal
            ? "Oprit"
            : "Apel activ",
      state:
        run.state === "QUEUED"
          ? "active"
          : failed
            ? "error"
            : revoked
              ? "revoked"
              : "complete",
    },
    {
      id: "stream",
      label: "Streaming",
      detail: streamed ? `${run.last_sequence} evenimente` : "În așteptare",
      state:
        run.state === "STREAMING"
          ? "active"
          : failed
            ? "error"
            : revoked
              ? "revoked"
              : streamed
                ? "complete"
                : "idle",
    },
    {
      id: "result",
      label: "Rezultat",
      detail:
        run.result_state === "PARTIAL"
          ? "Parțial"
          : run.result_state === "COMPLETE"
            ? "Complet"
            : "Indisponibil",
      state:
        run.state === "SUCCEEDED"
          ? "complete"
          : failed
            ? "error"
            : revoked
              ? "revoked"
              : terminal
                ? "error"
                : "idle",
    },
    {
      id: "usage",
      label: "Cost și audit",
      detail:
        run.usage_state === "RECONCILED"
          ? "Reconciliat"
          : run.usage_state === "PENDING_RECONCILIATION"
            ? "Reconciliere necesară"
            : "Rezervat",
      state:
        run.usage_state === "RECONCILED"
          ? "complete"
          : terminal
            ? "error"
            : "active",
    },
  ];
};
